<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in navigationRoutes.routes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : toWorkInProgress(route)"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${route.displayName}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <div class="w-full">
                <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                  {{ route.displayName }}
                  <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
                </VaSidebarItemTitle>
                <p v-if="route.description" class="mt-1 text-xs leading-4 opacity-80">
                  {{ route.description }}
                </p>
              </div>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="toWorkInProgress(childRoute)"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${route.displayName}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <div>
                  <VaSidebarItemTitle class="leading-5 font-semibold">
                    {{ childRoute.displayName }}
                  </VaSidebarItemTitle>
                  <p v-if="childRoute.description" class="mt-1 text-xs leading-4 opacity-80">
                    {{ childRoute.description }}
                  </p>
                </div>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import {
  useColors,
  VaAccordion,
  VaCollapse,
  VaIcon,
  VaSidebar,
  VaSidebarItem,
  VaSidebarItemContent,
  VaSidebarItemTitle,
} from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  components: { VaAccordion, VaCollapse, VaSidebarItem, VaIcon, VaSidebarItemTitle, VaSidebarItemContent, VaSidebar },
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const workInProgressRouteName = 'en-construccion'

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const selectedSection = computed(() => {
      const section = route.query.section
      return Array.isArray(section) ? section[0] : section
    })

    const toWorkInProgress = (target: INavigationRoute) => ({
      name: workInProgressRouteName,
      query: { section: target.name },
    })

    const isActiveBySection = (name: string) => route.name === workInProgressRouteName && selectedSection.value === name

    const isActiveChildRoute = (child: INavigationRoute) => isActiveBySection(child.name)

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return isActiveBySection(section.name)
      }

      return section.children.some(({ name }) => isActiveBySection(name))
    }

    const setActiveExpand = () =>
      (value.value = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      toWorkInProgress,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
